import { useCallback, useState } from 'react'
import { Box, Typography } from '@mui/material'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { useAppActions } from '../AppContext'
import { addToast } from '../services/Toasts'
import { runScore } from '../services/QuestionActions'
import { pmSaveQuestion } from '../services/ProjectManagerService'
import TaActionButton from './TaActionButton'
import TaQueryBar from './TaQueryBar'
import TaPadlock from './TaPadlock'
import TaThreeDots from './TaThreeDots'
import QuestionBadge from './QuestionBadge'
import BidQuestionScoringTab from './BidQuestionScoringTab'
import TaEvalImprovements from './TaEvalImprovements'
import GenerateScoringButton from './buttons/GenerateScoringButton'
import SuggestImprovementsButton from './buttons/SuggestImprovementsButton'
import OpenInScoreFixButton from './buttons/OpenInScoreFixButton'
import taapi from '../services/taapi'
import { FS_XS, ICON_SM } from '../common/theme'
import { questionAnchorId, type Project, type Question } from '../types'

interface BidQuestionScoringProps {
  question: Question
  project: Project
  index?: number
}

// Per-question scoring row used inside BidScoringTab. Mirrors the angularjs
// `taProjectItemQuestionScoring` directive: sticky question header with
// Estimate Score / Suggest Improvements / Score & Fix / Save, plus a
// two-column body showing TaEvalScored on the left and TaEvalImprovements
// on the right. Intentionally lighter than BidQuestion - no generate, no
// sources/storyboard/answer editors. Use BidQuestion (Builder/Manager) for
// answer editing; this view is for evaluation only.
export default function BidQuestionScoring({ question, project, index = 0 }: BidQuestionScoringProps) {
  const { updateQuestion } = useAppActions()

  const [saving, setSaving] = useState(false)
  // `handleImprove` (used by TaEvalImprovements below) keeps its own
  // local running state; the toolbar's SuggestImprovementsButton owns
  // its own state independently.
  const [improving, setImproving] = useState(false)

  const patchQ = useCallback((patch: Partial<Question>) => {
    updateQuestion(question.questionsha, patch)
  }, [question.questionsha, updateQuestion])

  const handleScore = useCallback(async () => {
    try {
      const result = await runScore(question, project, updateQuestion)
      if (result.success) {
        addToast('success', 'Score estimated')
      } else {
        addToast('error', 'Failed to estimate score')
      }
    } catch {
      addToast('error', 'Failed to score question')
    }
  }, [question, project, addToast, patchQ])

  const handleImprove = useCallback(async () => {
    if (improving) return
    setImproving(true)
    try {
      const result = await taapi.post('/api2/rbimprove', {
        questionsha: question.questionsha,
        bideval: (question as any).bideval,
      })
      if (result?.bideval) patchQ({ bideval: result.bideval } as Partial<Question>)
      addToast('success', 'Suggestions ready')
    } catch {
      addToast('error', 'Failed to suggest improvements')
    } finally {
      setImproving(false)
    }
  }, [question, improving, patchQ, addToast])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await pmSaveQuestion({ ...question, projectsha: project.projectsha })
      patchQ({ modified: false })
      addToast('success', 'Question saved')
    } catch {
      addToast('error', 'Failed to save question')
    } finally {
      setSaving(false)
    }
  }, [question, project, addToast, patchQ])

  const handleApplyFix = useCallback((improvement: any) => {
    improvement.fix = true
    patchQ({ bideval: { ...(question as any).bideval } } as Partial<Question>)
  }, [question, patchQ])

  const handleDeleteFix = useCallback((improvement: any) => {
    improvement.fix = false
    patchQ({ bideval: { ...(question as any).bideval } } as Partial<Question>)
  }, [question, patchQ])

  const groups = (question as any).bideval?.checkpointeval?.groups ?? []
  const allImprovements = groups.flatMap((g: any) =>
    (g.improvementcategories ?? []).flatMap((c: any) => c.improvements ?? [])
  )

  const handleClearFixes = useCallback(() => {
    allImprovements.forEach((imp: any) => { imp.fix = false })
    patchQ({ bideval: { ...(question as any).bideval } } as Partial<Question>)
  }, [allImprovements, question, patchQ])

  return (
    <Box
      id={questionAnchorId(question.questionsha)}
      sx={{ borderBottom: 1, borderColor: 'divider', scrollMarginTop: 4 }}
    >
      {/* Sticky toolbar */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.4, gap: 0.5, flexWrap: 'wrap' }}>
          <QuestionBadge
            numbering={(question as any).numbering}
            fallback={index + 1}
            testIdPrefix="pmqsc_badge"
            onCommit={async (v: string) => {
              if (v === ((question as any).numbering ?? '')) return
              patchQ({ numbering: v } as Partial<Question>)
              try {
                await pmSaveQuestion({ ...question, numbering: v, projectsha: project.projectsha })
                patchQ({ modified: false })
              } catch {
                addToast('error', 'Failed to save numbering')
              }
            }}
          />

          <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600, fontSize: FS_XS, opacity: 0.65 }}>
            Question
          </Typography>
          <TaPadlock
            isLocked={(question as any).locks?.question ?? false}
            onToggle={() => patchQ({
              locks: {
                ...(question as any).locks,
                question: !((question as any).locks?.question ?? false),
              } as any,
            })}
          />

          <Box sx={{ flex: 1 }} />

          <GenerateScoringButton question={question} />

          <SuggestImprovementsButton question={question} />

          <OpenInScoreFixButton question={question} />

          <TaActionButton
            title={saving ? 'Saving...' : (question as any).modified ? 'Save (unsaved changes)' : 'No changes to save'}
            onClick={handleSave}
            disabled={!(question as any).modified}
            running={saving}
            icon={<SaveOutlinedIcon sx={{ fontSize: ICON_SM }} />}
            color={(question as any).modified ? 'primary.main' : undefined}
          />

          <TaThreeDots
            droplist={[
              { label: 'Clear Score', icon: <AssessmentOutlinedIcon sx={{ fontSize: ICON_SM }} />, onClick: () => patchQ({ bideval: undefined as any }), danger: true },
            ]}
            index={index}
          />
        </Box>

        <Box sx={{ px: 1.5, pb: 0.75 }}>
          <TaQueryBar
            htmlContent={(question as any).question ?? ''}
            onChange={(html) => patchQ({ question: html } as Partial<Question>)}
            editable={!(question as any).locks?.question}
            placeholder="Enter your question here"
            id={`pmqscquestion_${index}`}
          />
        </Box>
      </Box>

      {/* Two-column eval body */}
      <Box sx={{ display: 'flex', minHeight: 200 }}>
        <Box sx={{ flex: 1, minWidth: 0, borderRight: 1, borderColor: 'divider' }}>
          {/* Full scoring tab — header (padlock + score-type +
              ScoreArc + Clear) plus TaEvalScored body. Mirrors what
              BidQuestion mounts inside its left-side scoring tab so
              the row in the project scoring view is consistent. */}
          <BidQuestionScoringTab
            question={question}
            onScore={handleScore}
            isLocked={!!(question as any).locks?.scoring}
            onToggleLock={() => patchQ({
              locks: {
                ...((question as any).locks ?? {}),
                scoring: !((question as any).locks?.scoring ?? false),
              } as any,
            })}
            onClear={() => patchQ({ bideval: undefined as any })}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TaEvalImprovements
            improvements={allImprovements}
            question={question}
            onImprove={handleImprove}
            onFixAll={() => {
              allImprovements.forEach((imp: any) => { if (imp.improvtypestring) imp.fix = true })
              patchQ({ bideval: { ...(question as any).bideval } } as Partial<Question>)
            }}
            onApplyFix={handleApplyFix}
            onDeleteFix={handleDeleteFix}
            onClearFixes={handleClearFixes}
          />
        </Box>
      </Box>
    </Box>
  )
}
